import {
	Dtos,
	type FinancialPeriodPreparationMethod,
	formatToDollar,
	formatToPercent,
	type ISODate,
	type ISODateTime,
	type PeriodType,
	type WipProjectId,
	type WipSchedule,
	type WorkInProgressScheduleExtractionId,
} from "@inrev/common";
import { Option } from "@inrev/core";
import { match } from "ts-pattern";
import { formatEmpty } from "../../../../../utils/string";
import {
	deriveWipPortfolioTotals,
	type WipAnalysisGridMetricSource,
} from "./deriveWipPortfolioTotals";
import {
	WIP_ANALYSIS_GRID_METRICS,
	type WipAnalysisGridMetricFormat,
	type WipAnalysisGridMetricId,
} from "./wipMetric";
import {
	formatWipPeriodDateLabel,
	formatWipPeriodTypeLabel,
	latestScheduleOfRecordExtractionId,
} from "./wipPeriod";

export const WIP_ANALYSIS_GRID_METRIC_COLUMN_WIDTH = "110px";

export const WIP_ANALYSIS_PORTFOLIO_TOTAL_LABEL = "Portfolio Total";

export type WipAnalysisGridPeriod = {
	extractionId: WorkInProgressScheduleExtractionId;
	fileId: Dtos.Wip.Extraction.Get.Response["fileId"];
	statementDate: ISODate | null;
	dateLabel: string;
	periodTypeLabel: string;
	periodType: PeriodType | null;
	preparationMethod: FinancialPeriodPreparationMethod | null;
	underwriterReviewedAt: ISODateTime | null;
	/** Rows with no project. They count toward the total row but have no project row. */
	unassignedLineItemCount: number;
};

export type WipAnalysisGridMetricColumn = {
	id: string;
	metricId: WipAnalysisGridMetricId;
	label: string;
	width: string;
};

export type WipAnalysisGridPeriodGroup = {
	id: WorkInProgressScheduleExtractionId;
	period: WipAnalysisGridPeriod;
	columns: ReadonlyArray<WipAnalysisGridMetricColumn>;
};

export type WipAnalysisGridCell = {
	raw: number | null;
	display: string;
	metricId: WipAnalysisGridMetricId;
};

type WipAnalysisGridRowCells = Record<string, WipAnalysisGridCell>;

export type WipAnalysisGridProjectRow = {
	kind: "project";
	projectId: WipProjectId;
	projectNumber: string | null;
	projectName: string | null;
	jobStatus: WipSchedule.WipJobStatus | null;
	cells: WipAnalysisGridRowCells;
};

export type WipAnalysisGridTotalRow = {
	kind: "total";
	label: string;
	cells: WipAnalysisGridRowCells;
};

export type WipAnalysisGridViewModel = {
	periodGroups: ReadonlyArray<WipAnalysisGridPeriodGroup>;
	projectRows: ReadonlyArray<WipAnalysisGridProjectRow>;
	totalRow: WipAnalysisGridTotalRow;
};

export const wipAnalysisGridCellKey = (
	extractionId: WorkInProgressScheduleExtractionId,
	metricId: WipAnalysisGridMetricId,
): string => `${extractionId}::${metricId}`;

const EMPTY_DISPLAY = formatEmpty(null);

type FormatCurrencyOptions = {
	abbreviate: boolean;
	omitSymbol: boolean;
};

const formatCurrency = (value: number | null, options: FormatCurrencyOptions): string => {
	if (Option.isNone(value)) return EMPTY_DISPLAY;
	const formatted = formatToDollar(value, {
		accounting: true,
		round: !options.abbreviate,
		abbreviate: options.abbreviate,
		omitSymbol: options.omitSymbol,
	});
	return Option.isNone(formatted) ? EMPTY_DISPLAY : formatted;
};

const PROJECT_ROW_CURRENCY_OPTIONS: FormatCurrencyOptions = {
	abbreviate: false,
	omitSymbol: true,
};

const TOTAL_ROW_CURRENCY_OPTIONS: FormatCurrencyOptions = {
	abbreviate: true,
	omitSymbol: false,
};

const formatWipRatioPercent = (value: number | null): string =>
	formatToPercent(value, 1, EMPTY_DISPLAY);

const formatWipCompletePercent = (value: number | null): string => {
	if (Option.isNone(value)) return EMPTY_DISPLAY;
	return `${value.toFixed(1)}%`;
};

const formatMetricDisplay = (
	format: WipAnalysisGridMetricFormat,
	value: number | null,
	options: FormatCurrencyOptions,
): string =>
	match(format)
		.with("currency", () => formatCurrency(value, options))
		.with("ratio-percent", () => formatWipRatioPercent(value))
		.with("complete-percent", () => formatWipCompletePercent(value))
		.exhaustive();

type LineItemRead = Dtos.Wip.LineItem.Get.Response;

const metricValue = (
	source: WipAnalysisGridMetricSource,
	metricId: WipAnalysisGridMetricId,
): number | null =>
	match(metricId)
		.with("contract", () => source.contractTotalAmount)
		.with("est-cost", () => source.totalEstimatedCost)
		.with("billed-td", () => source.totalBilledToDate)
		.with("cost-td", () => source.totalCostToDate)
		.with("est-ctc", () => source.estimatedCostToComplete)
		.with("est-gp", () => source.estimatedGrossProfit)
		.with("gp-percent", () => source.grossProfitPercent)
		.with("pct-comp", () => source.percentComplete)
		.with("gp-earned", () => source.grossProfitEarnedToDate)
		.with("gp-to-be", () => source.grossProfitToBeEarned)
		.with("underbillings", () => source.underbillings)
		.with("overbillings", () => source.overbillings)
		.with("pure-job-borrow", () => source.pureJobBorrow)
		.with("cash-flow-remaining", () => source.cashFlowRemaining)
		.exhaustive();

const buildCellsForMetrics = (
	extractionId: WorkInProgressScheduleExtractionId,
	getValue: (metricId: WipAnalysisGridMetricId) => number | null,
	options: FormatCurrencyOptions,
): WipAnalysisGridRowCells =>
	Object.fromEntries(
		WIP_ANALYSIS_GRID_METRICS.map((metric) => {
			const value = getValue(metric.id);
			return [
				wipAnalysisGridCellKey(extractionId, metric.id),
				{
					raw: value,
					display: formatMetricDisplay(metric.format, value, options),
					metricId: metric.id,
				},
			];
		}),
	);

type LineItemsByProject = ReadonlyMap<WipProjectId, LineItemRead>;

export type WipAnalysisLineItemIndex = {
	byExtraction: ReadonlyMap<WorkInProgressScheduleExtractionId, ReadonlyArray<LineItemRead>>;
	byExtractionAndProject: ReadonlyMap<WorkInProgressScheduleExtractionId, LineItemsByProject>;
	unassignedCountByExtraction: ReadonlyMap<WorkInProgressScheduleExtractionId, number>;
};

export const indexWipAnalysisLineItems = (
	lineItems: ReadonlyArray<LineItemRead>,
): WipAnalysisLineItemIndex => {
	const byExtraction = new Map<WorkInProgressScheduleExtractionId, LineItemRead[]>();
	const byExtractionAndProject = new Map<
		WorkInProgressScheduleExtractionId,
		Map<WipProjectId, LineItemRead>
	>();
	const unassignedCountByExtraction = new Map<WorkInProgressScheduleExtractionId, number>();
	for (const lineItem of lineItems) {
		const extractionLineItems = byExtraction.get(lineItem.extractionId) ?? [];
		extractionLineItems.push(lineItem);
		byExtraction.set(lineItem.extractionId, extractionLineItems);

		if (Option.isNone(lineItem.projectId)) {
			unassignedCountByExtraction.set(
				lineItem.extractionId,
				(unassignedCountByExtraction.get(lineItem.extractionId) ?? 0) + 1,
			);
			continue;
		}
		const byProject =
			byExtractionAndProject.get(lineItem.extractionId) ?? new Map<WipProjectId, LineItemRead>();
		byProject.set(lineItem.projectId, lineItem);
		byExtractionAndProject.set(lineItem.extractionId, byProject);
	}
	return { byExtraction, byExtractionAndProject, unassignedCountByExtraction };
};

const EMPTY_LINE_ITEMS: ReadonlyArray<LineItemRead> = [];

const buildPeriodGroups = (
	visibleExtractions: ReadonlyArray<Dtos.Wip.Extraction.Get.Response>,
	unassignedCounts: ReadonlyMap<WorkInProgressScheduleExtractionId, number>,
): WipAnalysisGridPeriodGroup[] =>
	visibleExtractions.map((extraction) => {
		const statementDate = Option.toNull(extraction.statementDate);
		const periodType = Option.toNull(extraction.periodType);
		const preparationMethod = Option.toNull(extraction.preparationMethod);
		const period: WipAnalysisGridPeriod = {
			extractionId: extraction.id,
			fileId: extraction.fileId,
			statementDate,
			dateLabel: formatWipPeriodDateLabel(extraction.statementDate),
			periodTypeLabel: formatWipPeriodTypeLabel(periodType, preparationMethod),
			periodType,
			preparationMethod,
			underwriterReviewedAt: Option.toNull(extraction.underwriterReviewedAt),
			unassignedLineItemCount: unassignedCounts.get(extraction.id) ?? 0,
		};

		return {
			id: extraction.id,
			period,
			columns: WIP_ANALYSIS_GRID_METRICS.map((metric) => ({
				id: wipAnalysisGridCellKey(extraction.id, metric.id),
				metricId: metric.id,
				label: metric.label,
				width: WIP_ANALYSIS_GRID_METRIC_COLUMN_WIDTH,
			})),
		};
	});

const jobStatusFromScheduleOfRecord = (
	projectId: WipProjectId,
	scheduleOfRecordLineItems: Option<LineItemsByProject>,
): WipSchedule.WipJobStatus | null =>
	Option.mapOrElse(
		Option.flatMap(scheduleOfRecordLineItems, (byProject) => byProject.get(projectId)),
		(lineItem) => lineItem.jobStatus,
		null,
	);

const buildProjectRows = (
	projects: ReadonlyArray<Dtos.Wip.Project.Get.Response>,
	visibleExtractions: ReadonlyArray<Dtos.Wip.Extraction.Get.Response>,
	lineItemIndex: WipAnalysisLineItemIndex["byExtractionAndProject"],
	scheduleOfRecordExtractionId: Option<WorkInProgressScheduleExtractionId>,
): WipAnalysisGridProjectRow[] => {
	const projectIdsWithVisibleLineItems = new Set(
		visibleExtractions.flatMap((extraction) =>
			Option.mapOrElse(lineItemIndex.get(extraction.id), (byProject) => [...byProject.keys()], []),
		),
	);
	const scheduleOfRecordLineItems = Option.flatMap(scheduleOfRecordExtractionId, (extractionId) =>
		lineItemIndex.get(extractionId),
	);

	return projects
		.filter((project) => projectIdsWithVisibleLineItems.has(project.id))
		.map((project) => {
			const cells: WipAnalysisGridRowCells = {};
			for (const extraction of visibleExtractions) {
				const lineItem = Option.flatMap(lineItemIndex.get(extraction.id), (byProject) =>
					byProject.get(project.id),
				);
				Object.assign(
					cells,
					buildCellsForMetrics(
						extraction.id,
						(metricId) => Option.mapOrElse(lineItem, (item) => metricValue(item, metricId), null),
						PROJECT_ROW_CURRENCY_OPTIONS,
					),
				);
			}

			return {
				kind: "project",
				projectId: project.id,
				projectNumber: project.projectNumber,
				projectName: project.name,
				jobStatus: jobStatusFromScheduleOfRecord(project.id, scheduleOfRecordLineItems),
				cells,
			};
		});
};

const buildTotalRow = (
	visibleExtractions: ReadonlyArray<Dtos.Wip.Extraction.Get.Response>,
	lineItemsByExtraction: WipAnalysisLineItemIndex["byExtraction"],
): WipAnalysisGridTotalRow => {
	const cells: WipAnalysisGridRowCells = {};
	for (const extraction of visibleExtractions) {
		const totals = deriveWipPortfolioTotals(
			lineItemsByExtraction.get(extraction.id) ?? EMPTY_LINE_ITEMS,
		);
		Object.assign(
			cells,
			buildCellsForMetrics(
				extraction.id,
				(metricId) => metricValue(totals, metricId),
				TOTAL_ROW_CURRENCY_OPTIONS,
			),
		);
	}

	return {
		kind: "total",
		label: WIP_ANALYSIS_PORTFOLIO_TOTAL_LABEL,
		cells,
	};
};

/** One period column per extraction, in the order given. */
export function buildWipAnalysisGridViewModel(
	extractions: ReadonlyArray<Dtos.Wip.Extraction.Get.Response>,
	lineItemIndex: WipAnalysisLineItemIndex,
	projects: ReadonlyArray<Dtos.Wip.Project.Get.Response>,
	statements: ReadonlyArray<Dtos.Wip.Statement.Get.Response>,
): WipAnalysisGridViewModel {
	return {
		periodGroups: buildPeriodGroups(extractions, lineItemIndex.unassignedCountByExtraction),
		projectRows: buildProjectRows(
			projects,
			extractions,
			lineItemIndex.byExtractionAndProject,
			latestScheduleOfRecordExtractionId(statements),
		),
		totalRow: buildTotalRow(extractions, lineItemIndex.byExtraction),
	};
}
